import {
  IMDL_SUBMISSION_SOURCE,
  Submission,
  SubmissionStatus,
} from '../../types/models';

/**
 * An inter-model data load is handed out up to 3 times, 15 minutes apart (SQS
 * visibility timeout 900 s, maxReceiveCount 3), and holds its group's lock
 * while it runs. A row that is still Started and locked after this long has
 * not finished and will not on its own: the lock can only be cleared by
 * support. One hour is the 45 minutes of retries plus a margin.
 */
export const IMDL_DID_NOT_FINISH_AFTER_MINUTES = 60;

export type ImdlCardState = 'noNewData' | 'didNotFinish';

/**
 * What the submission card should say about an inter-model data load row, or
 * null when it is not one or has nothing special to say. Only rows the API
 * marks as written by IMDL (SubmissionSource 4) are affected; every other row
 * keeps its existing look.
 *
 * - noNewData: the load finished (status -5) without a new version. Not a
 *   failure and nothing to delete.
 * - didNotFinish: still Started, still locked and old. A row with no
 *   updated_at, or an unreadable one, is never reported: the card does not
 *   claim a fault it cannot date.
 */
export const getImdlCardState = (
  submission: Submission,
  now: Date = new Date()
): ImdlCardState | null => {
  if (submission.SubmissionSource !== IMDL_SUBMISSION_SOURCE) {
    return null;
  }

  if (submission.Status === SubmissionStatus.NoDataReturned) {
    return 'noNewData';
  }

  if (
    submission.Status === SubmissionStatus.Started &&
    submission.IsLocked &&
    submission.updated_at
  ) {
    const updatedAt = Date.parse(submission.updated_at);
    const ageMinutes = (now.getTime() - updatedAt) / 60000;
    if (
      !Number.isNaN(updatedAt) &&
      ageMinutes >= IMDL_DID_NOT_FINISH_AFTER_MINUTES
    ) {
      return 'didNotFinish';
    }
  }

  return null;
};

export const IMDL_NO_NEW_DATA_TITLE = 'Nothing new to load';

export const IMDL_DID_NOT_FINISH_TITLE = 'Inter-model load did not finish';

export const imdlDidNotFinishMessage = (submissionId: number): string =>
  `This load started more than an hour ago and has not finished, so saves to this group stay blocked. Contact Trinity support and quote submission ${submissionId}.`;
